/**
 * web/src/hooks/mutations/useTriggerWorkflow.ts
 * Admin-only on the server (requireRole('admin')). Surfaces 403 via toast.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../../lib/queryKeys';
import { toast } from 'sonner';
import { triggerWorkflow, type TriggerWorkflowBody } from '../../api/workflows';
import { toastApiError } from '../../lib/api';

/**
 * Triggers a new workflow. Cache invalidations on success bring the new
 * row into the workflow list immediately (without waiting for the WS
 * `workflow_started` event).
 *
 * @returns React Query mutation handle
 * @sideEffects HTTP POST; engine begins async step execution
 * @idempotent no — duplicate (eventId, triggerSource) returns 409
 */
export function useTriggerWorkflow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TriggerWorkflowBody) => triggerWorkflow(body),
    onSuccess: () => {
      toast.success('Workflow triggered');
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
    },
    onError: (err) => {
      toastApiError(err, 'Trigger failed');
    },
  });
}
